"use client"

import { React, useState, useEffect } from "react"
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card"
import { Button } from "../../ui/button"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../ui/tabs"
import { Avatar, AvatarFallback, AvatarImage } from "../../ui/avatar"
import { ThumbsUp, ThumbsDown, MessageSquare, Edit, ListChecks, Plus, Trash2, UserPlus, QrCode, AlertTriangle } from 'lucide-react'
import { Textarea } from "../../ui/textarea"
import { Input } from "../../ui/input"
import { Label } from "../../ui/label"
import { useAuth0 } from "@auth0/auth0-react"
import { parseApiError } from "../../../lib/apiErrors"
import { FEATURE_FLAGS, isFeatureEnabled } from "../../../config/featureFlags"

export const formatFundingAmount = (amount, currency = "USD") => {
  if (amount === null || amount === undefined || amount === "") {
    return null
  }

  const numericAmount = Number(amount)
  if (!Number.isFinite(numericAmount)) {
    return `${amount} ${currency || "USD"}`
  }

  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency || "USD",
    maximumFractionDigits: 2,
  }).format(numericAmount)
}

export const formatBtcPaymentAmount = (amount) => {
  if (amount === null || amount === undefined || amount === "") {
    return null
  }

  const numericAmount = Number(amount)
  if (!Number.isFinite(numericAmount)) {
    return `${amount} BTC`
  }

  return `${numericAmount.toFixed(8)} BTC`
}

export const shortenPaymentIdentifier = (value) => {
  if (!value) {
    return null
  }

  const text = String(value)
  if (text.length <= 18) {
    return text
  }

  return `${text.slice(0, 8)}...${text.slice(-8)}`
}

const paymentStatusLabels = {
  Draft: "Draft",
  Queued: "Queued",
  Processing: "Processing",
  Sent: "Broadcast",
  Broadcast: "Broadcast",
  Confirming: "Confirming",
  Confirmed: "Confirmed",
  RetryPending: "Retry pending",
  Failed: "Failed",
}

export const getTaskPayoutSummary = (task) => {
  const amount = formatBtcPaymentAmount(task?.paymentAmount)
  const paymentStatus = task?.paymentStatus || (task?.paymentIntentId ? "Queued" : null)

  if (!amount && !paymentStatus && !task?.paymentRecipientAddress) {
    return null
  }

  const confirmationCount = Number(task?.paymentConfirmationCount ?? 0)
  const requiredConfirmations = Number(task?.paymentRequiredConfirmations ?? 0)
  const hasConfirmations =
    Number.isFinite(confirmationCount) && Number.isFinite(requiredConfirmations) && requiredConfirmations > 0

  return {
    amount,
    status: paymentStatusLabels[paymentStatus] || paymentStatus || "Not queued",
    confirmations: hasConfirmations ? `${confirmationCount}/${requiredConfirmations} confirmations` : null,
    transactionId: shortenPaymentIdentifier(task?.paymentTransactionId),
    recipient: shortenPaymentIdentifier(task?.paymentRecipientAddress),
    failureReason: task?.paymentFailureReason || null,
  }
}

export const createEmptyTaskSuggestion = () => ({
  title: "",
  description: "",
  priority: "medium",
  estimatedHours: "",
  assignedTo: "",
  executorId: "",
  fundingNeededAmount: "",
  fundingCurrency: "USD",
  fundingStatus: "",
  materialsNeeded: "",
  materialsStatus: "",
  volunteerSlotsNeeded: "",
  volunteerSlotsFilled: "",
  receiptRequired: false,
  receiptStatus: "",
  paymentAmount: "",
  paymentRecipientAddress: "",
})

const normalizeOptionalNumber = (value) => {
  if (value === null || value === undefined || value === "") {
    return null
  }

  const numericValue = Number(value)
  return Number.isFinite(numericValue) && numericValue > 0 ? numericValue : null
}

export const buildTaskSuggestionPayload = (tasks) => ({
  tasks: tasks.map((task) => ({
    ...task,
    estimatedHours: normalizeOptionalNumber(task.estimatedHours),
    executorId: normalizeOptionalNumber(task.executorId),
    fundingNeededAmount: normalizeOptionalNumber(task.fundingNeededAmount),
    volunteerSlotsNeeded: normalizeOptionalNumber(task.volunteerSlotsNeeded),
    volunteerSlotsFilled: normalizeOptionalNumber(task.volunteerSlotsFilled),
    paymentAmount: normalizeOptionalNumber(task.paymentAmount),
  })),
})

export const getTaskReadinessSummary = (task) => {
  if (String(task?.status || "").toLowerCase() === "skipped") {
    return [
      {
        label: "Not needed",
        value: task?.skipReason || task?.completionProofNote || "The group decided this item is not needed anymore.",
        complete: true,
      },
    ]
  }

  const items = []
  const fundingAmount = formatFundingAmount(task?.fundingNeededAmount, task?.fundingCurrency || "USD")

  if (fundingAmount) {
    const shortfall = Number(task?.fundingShortfall || 0)
    items.push({
      label: "Funding",
      value: shortfall > 0
        ? `${formatFundingAmount(shortfall, task?.fundingCurrency || "USD")} still needed`
        : `${fundingAmount} - ${task?.fundingStatus || "Needed"}`,
      complete: shortfall <= 0 && ["funded", "confirmed", "received", "waived"].includes(String(task?.fundingStatus || "").toLowerCase()),
    })
  }

  if (task?.materialsNeeded) {
    items.push({
      label: "Materials",
      value: `${task.materialsNeeded} - ${task.materialsStatus || "Needed"}`,
      complete: ["received", "available", "confirmed", "waived"].includes(String(task?.materialsStatus || "").toLowerCase()),
    })
  }

  if (Number(task?.volunteerSlotsNeeded || 0) > 0) {
    const filled = Number(task?.volunteerSlotsFilled || 0)
    const needed = Number(task.volunteerSlotsNeeded)
    items.push({
      label: "People",
      value: `${filled}/${needed} volunteer spots filled`,
      complete: filled >= needed,
    })
  }

  if (task?.receiptRequired) {
    items.push({
      label: "Receipt/proof",
      value: task?.receiptStatus || "Needed",
      complete: ["attached", "uploaded", "waived", "not needed"].includes(String(task?.receiptStatus || "").toLowerCase()),
    })
  }

  return items
}

export const getTaskExecutionCommands = (task) => {
  const status = String(task?.status || "").toLowerCase()
  const commands = []

  if (!status || status === "not started") {
    commands.push({ command: "accept", label: "Accept" })
  }

  if (status === "not started" || status === "accepted") {
    commands.push({ command: "start", label: "Start" })
  }

  if (status === "accepted" || status === "in progress") {
    commands.push({ command: "complete", label: "Complete" })
  }

  if (status === "completed pending approval" || status === "done pending proof" || status === "payout queued" || status === "complete approved") {
    commands.push({ command: "approve-completion", label: "Approve Completion" })
  }

  if (!["skipped", "complete approved", "payout queued"].includes(status)) {
    commands.push({ command: "skip", label: "Mark not needed" })
  }

  return commands
}

const TaskListModule = ({ actionId, groupId }) => {
  const bitcoinEnabled = isFeatureEnabled(FEATURE_FLAGS.bitcoinExperience)
  const [tasks, setTasks] = useState([])
  const [suggestions, setSuggestions] = useState([])
  const [comments, setComments] = useState([])
  const [newSuggestion, setNewSuggestion] = useState([createEmptyTaskSuggestion()])
  const [newComment, setNewComment] = useState("")
  const [activeTab, setActiveTab] = useState("current")
  const [loading, setLoading] = useState(true)
  const [members, setMembers] = useState([])
  const [taskCommandError, setTaskCommandError] = useState("")
  const [completePaymentRecipients, setCompletePaymentRecipients] = useState({})
  const [completionProof, setCompletionProof] = useState({})
  const [skipReasons, setSkipReasons] = useState({})
  const [assignmentForms, setAssignmentForms] = useState({})
  const apiUrl = process.env.REACT_APP_API_URL
  const { getAccessTokenSilently } = useAuth0()

  useEffect(() => {
    fetchTasksData()
    fetchGroupMembers()
  }, [actionId, groupId])

  const fetchTasksData = async () => {
    try {
      setLoading(true)
      const token = await getAccessTokenSilently({
        authorizationParams: {
          audience: process.env.REACT_APP_API_AUDIENCE,
        },
      })

      // Fetch the current tasks
      const tasksResponse = await fetch(`${apiUrl}/api/Actions/${actionId}/tasks`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })

      if (tasksResponse.ok) {
        const tasksData = await tasksResponse.json()
        setTasks(tasksData || [])
      }

      // Fetch suggestions
      const suggestionsResponse = await fetch(`${apiUrl}/api/Actions/${actionId}/tasks/suggestions`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })

      if (suggestionsResponse.ok) {
        const suggestionsData = await suggestionsResponse.json()
        setSuggestions(suggestionsData || [])
      }

      // Fetch comments
      const commentsResponse = await fetch(`${apiUrl}/api/Actions/${actionId}/tasks/comments`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })

      if (commentsResponse.ok) {
        const commentsData = await commentsResponse.json()
        setComments(commentsData || [])
      }
    } catch (error) {
      console.error("Error fetching tasks data:", error)
    } finally {
      setLoading(false)
    }
  }

  const fetchGroupMembers = async () => {
    try {
      const token = await getAccessTokenSilently({
        authorizationParams: {
          audience: process.env.REACT_APP_API_AUDIENCE,
        },
      })

      const response = await fetch(`${apiUrl}/api/GroupsTbls/${groupId}/members`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })

      if (response.ok) {
        const data = await response.json()
        setMembers(data || [])
      }
    } catch (error) {
      console.error("Error fetching group members:", error)
    }
  }

  const handleAddTask = () => {
    setNewSuggestion([
      ...newSuggestion,
      createEmptyTaskSuggestion(),
    ])
  }

  const handleRemoveTask = (index) => {
    if (newSuggestion.length > 1) {
      setNewSuggestion(newSuggestion.filter((_, i) => i !== index))
    }
  }

  const handleTaskChange = (index, field, value) => {
    const updatedTasks = [...newSuggestion]
    updatedTasks[index][field] = field === "receiptRequired" ? Boolean(value) : value
    if (field === "assignedTo" && !updatedTasks[index].executorId) {
      updatedTasks[index].executorId = value
    }
    setNewSuggestion(updatedTasks)
  }

  const handleSubmitSuggestion = async () => {
    // Validate that at least one task has a title
    if (!newSuggestion.some((item) => item.title.trim())) return

    try {
      const token = await getAccessTokenSilently({
        authorizationParams: {
          audience: process.env.REACT_APP_API_AUDIENCE,
        },
      })

      const response = await fetch(`${apiUrl}/api/Actions/${actionId}/tasks/suggestions`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(buildTaskSuggestionPayload(newSuggestion)),
      })

      if (response.ok) {
        setNewSuggestion([createEmptyTaskSuggestion()])
        fetchTasksData() // Refresh data
      } else {
        console.error("Failed to submit suggestion")
      }
    } catch (error) {
      console.error("Error submitting suggestion:", error)
    }
  }

  const handleSubmitComment = async () => {
    if (!newComment.trim()) return

    try {
      const token = await getAccessTokenSilently({
        authorizationParams: {
          audience: process.env.REACT_APP_API_AUDIENCE,
        },
      })

      const response = await fetch(`${apiUrl}/api/Actions/${actionId}/tasks/comments`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ content: newComment }),
      })

      if (response.ok) {
        setNewComment("")
        fetchTasksData() // Refresh data
      } else {
        console.error("Failed to submit comment")
      }
    } catch (error) {
      console.error("Error submitting comment:", error)
    }
  }

  const handleVote = async (suggestionId, voteType) => {
    try {
      const token = await getAccessTokenSilently({
        authorizationParams: {
          audience: process.env.REACT_APP_API_AUDIENCE,
        },
      })

      const response = await fetch(`${apiUrl}/api/Actions/${actionId}/tasks/suggestions/${suggestionId}/vote`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ voteType }),
      })

      if (response.ok) {
        fetchTasksData() // Refresh data
      } else {
        console.error("Failed to submit vote")
      }
    } catch (error) {
      console.error("Error submitting vote:", error)
    }
  }

  const handleTaskCommand = async (task, command) => {
    setTaskCommandError("")

    try {
      const token = await getAccessTokenSilently({
        authorizationParams: {
          audience: process.env.REACT_APP_API_AUDIENCE,
        },
      })

      const body =
        command === "skip"
          ? {
              reason: skipReasons[task.id] || "",
            }
          : command === "complete"
          ? {
              paymentRecipientAddress:
                completePaymentRecipients[task.id] || task.paymentRecipientAddress || "",
              fundingConfirmed: Boolean(completionProof[task.id]?.fundingConfirmed),
              materialsConfirmed: Boolean(completionProof[task.id]?.materialsConfirmed),
              volunteersConfirmed: Boolean(completionProof[task.id]?.volunteersConfirmed),
              receiptAttached: Boolean(completionProof[task.id]?.receiptAttached),
              completionProofNote: completionProof[task.id]?.completionProofNote || "",
            }
          : undefined

      const response = await fetch(`${apiUrl}/api/Actions/${actionId}/tasks/${task.id}/${command}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: body ? JSON.stringify(body) : undefined,
      })

      if (response.ok) {
        if (command === "skip") {
          setSkipReasons(({ [task.id]: _removed, ...remaining }) => remaining)
        }
        await fetchTasksData()
        return
      }

      const requestError = await parseApiError(response, "Task action failed.")
      setTaskCommandError(requestError.message || "Task action failed.")
    } catch (error) {
      console.error("Error updating task:", error)
      setTaskCommandError("Task action failed.")
    }
  }

  const handleSelfSignup = async (task) => {
    setTaskCommandError("")

    try {
      const token = await getAccessTokenSilently({
        authorizationParams: {
          audience: process.env.REACT_APP_API_AUDIENCE,
        },
      })

      const response = await fetch(`${apiUrl}/api/Actions/${actionId}/tasks/${task.id}/assignments/self`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      })

      if (response.ok) {
        await fetchTasksData()
        return
      }

      const requestError = await parseApiError(response, "Signup failed.")
      setTaskCommandError(requestError.message || "Signup failed.")
    } catch (error) {
      console.error("Error signing up for task:", error)
      setTaskCommandError("Signup failed.")
    }
  }

  const handleAssignHelper = async (task) => {
    const form = assignmentForms[task.id] || {}
    setTaskCommandError("")

    try {
      const token = await getAccessTokenSilently({
        authorizationParams: {
          audience: process.env.REACT_APP_API_AUDIENCE,
        },
      })

      const response = await fetch(`${apiUrl}/api/Actions/${actionId}/tasks/${task.id}/assignments`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          assignedUserId: form.assignedUserId ? Number(form.assignedUserId) : null,
          displayName: form.displayName || "",
          email: form.email || "",
          roleName: form.roleName || "",
          note: form.note || "",
        }),
      })

      if (response.ok) {
        setAssignmentForms(({ [task.id]: _removed, ...remaining }) => remaining)
        await fetchTasksData()
        return
      }

      const requestError = await parseApiError(response, "Assignment failed.")
      setTaskCommandError(requestError.message || "Assignment failed.")
    } catch (error) {
      console.error("Error assigning task helper:", error)
      setTaskCommandError("Assignment failed.")
    }
  }

  const getTaskSignupUrl = (task) => {
    if (typeof window === "undefined") {
      return task.signupPath || ""
    }

    return `${window.location.origin}${task.signupPath || `/actions/${actionId}/refinement?tab=tasks&taskId=${task.id}`}`
  }

  const getPriorityColor = (priority) => {
    switch (priority) {
      case "high":
        return "bg-red-100 text-red-800"
      case "medium":
        return "bg-yellow-100 text-yellow-800"
      case "low":
        return "bg-green-100 text-green-800"
      default:
        return "bg-gray-100 text-gray-800"
    }
  }

  if (loading) {
    return <div className="p-4">Loading task list data...</div>
  }

  return (
    <div>
      <CardHeader className="px-0 pt-0">
        <CardTitle>Task List</CardTitle>
      </CardHeader>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-4">
          <TabsTrigger value="current">Current</TabsTrigger>
          <TabsTrigger value="suggestions">Suggestions ({suggestions.length})</TabsTrigger>
          <TabsTrigger value="discussion">Discussion ({comments.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="current">
          <Card>
            <CardContent className="p-4">
              {tasks.length === 0 ? (
                <div className="text-center py-6">
                  <ListChecks className="h-12 w-12 text-blue-500 mx-auto mb-4" />
                  <p className="text-muted-foreground">No tasks have been created yet.</p>
                  <Button onClick={() => setActiveTab("suggestions")} variant="outline" className="mt-4">
                    <Edit className="mr-2 h-4 w-4" /> Suggest Tasks
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  {tasks.map((task, index) => {
                    const payoutSummary = getTaskPayoutSummary(task)
                    const readinessItems = getTaskReadinessSummary(task)
                    const commands = getTaskExecutionCommands(task)
                    const needsCompletionRecipient =
                      commands.some((item) => item.command === "complete") &&
                      Number(task.paymentAmount || 0) > 0 &&
                      !task.paymentRecipientAddress

                    return (
                      <div key={index} className="border rounded-lg p-4">
                        <div className="flex justify-between items-start">
                          <div>
                            <div className="flex items-center space-x-2">
                              <h3 className="font-medium">{task.title}</h3>
                              <span className={`text-xs px-2 py-1 rounded-full ${getPriorityColor(task.priority)}`}>
                                {task.priority}
                              </span>
                              {task.needsHelpers && (
                                <span className="inline-flex items-center gap-1 rounded-full bg-orange-100 px-2 py-1 text-xs font-semibold text-orange-800">
                                  <AlertTriangle className="h-3 w-3" /> Needs helpers
                                </span>
                              )}
                              {task.needsFunds && (
                                <span className="inline-flex items-center gap-1 rounded-full bg-yellow-100 px-2 py-1 text-xs font-semibold text-yellow-900">
                                  <AlertTriangle className="h-3 w-3" /> Needs funds
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-gray-600 mt-1">{task.description}</p>
                          </div>
                        </div>

                        <div className="mt-2 grid grid-cols-2 gap-2 text-sm">
                          <div>
                            <span className="font-medium">Estimated Hours:</span>{" "}
                            {task.estimatedHours || "Not specified"}
                          </div>
                          {task.assignedTo && (
                            <div>
                              <span className="font-medium">Assigned To:</span> {task.assignedTo}
                            </div>
                          )}
                          {task.readyStatus && (
                            <div>
                              <span className="font-medium">Readiness:</span> {task.readyStatus}
                            </div>
                          )}
                        </div>

                        {bitcoinEnabled && payoutSummary && (
                          <div className="mt-3 rounded-md border border-blue-100 bg-blue-50 p-3 text-sm">
                            <div className="font-medium text-blue-950">BTC payout</div>
                            <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-2 text-blue-950">
                              {payoutSummary.amount && (
                                <div>
                                  <span className="font-medium">Amount:</span> {payoutSummary.amount}
                                </div>
                              )}
                              <div>
                                <span className="font-medium">Status:</span> {payoutSummary.status}
                              </div>
                              {payoutSummary.confirmations && (
                                <div>
                                  <span className="font-medium">Confirmations:</span> {payoutSummary.confirmations}
                                </div>
                              )}
                              {payoutSummary.transactionId && (
                                <div>
                                  <span className="font-medium">Transaction:</span> {payoutSummary.transactionId}
                                </div>
                              )}
                              {payoutSummary.recipient && (
                                <div>
                                  <span className="font-medium">Recipient:</span> {payoutSummary.recipient}
                                </div>
                              )}
                            </div>
                            {payoutSummary.failureReason && (
                              <div className="mt-2 text-red-700">
                                <span className="font-medium">Failure:</span> {payoutSummary.failureReason}
                              </div>
                            )}
                          </div>
                        )}

                        {readinessItems.length > 0 && (
                          <div className="mt-3 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm">
                            <div className="font-medium text-amber-950">What needs to be ready</div>
                            <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-2 text-amber-950">
                              {readinessItems.map((item) => (
                                <div key={`${task.id}-${item.label}`} className="flex items-start gap-2">
                                  <span aria-hidden="true">{item.complete ? "Done" : "Need"}</span>
                                  <span>
                                    <span className="font-medium">{item.label}:</span> {item.value}
                                  </span>
                                </div>
                              ))}
                            </div>
                            {task.completionProofNote && (
                              <p className="mt-2 text-amber-900">
                                <span className="font-medium">Proof note:</span> {task.completionProofNote}
                              </p>
                            )}
                            {task.skipReason && (
                              <p className="mt-2 text-amber-900">
                                <span className="font-medium">Why it was skipped:</span> {task.skipReason}
                              </p>
                            )}
                          </div>
                        )}

                        {(Number(task.volunteerSlotsNeeded || 0) > 0 || (task.assignments || []).length > 0) && (
                          <div className="mt-3 rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-950">
                            <div className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
                              <div>
                                <div className="font-medium">Helpers</div>
                                <p>
                                  {(task.volunteerSlotsFilled || 0)}/{task.volunteerSlotsNeeded || 0} accepted
                                  {task.volunteersStillNeeded > 0 ? ` · ${task.volunteersStillNeeded} still needed` : " · fully staffed"}
                                </p>
                              </div>
                              <Button type="button" size="sm" variant="outline" onClick={() => handleSelfSignup(task)}>
                                <UserPlus className="mr-2 h-4 w-4" /> I can help
                              </Button>
                            </div>

                            {(task.assignments || []).length > 0 && (
                              <div className="mt-3 grid gap-2 md:grid-cols-2">
                                {task.assignments.map((assignment) => (
                                  <div key={assignment.id} className="rounded border border-emerald-200 bg-white/80 p-2">
                                    <div className="font-medium">
                                      {assignment.assignedUserName || assignment.displayName || assignment.email || "Helper"}
                                    </div>
                                    <div className="text-xs">
                                      {assignment.status}
                                      {assignment.roleName ? ` · ${assignment.roleName}` : ""}
                                      {assignment.assignedByUserName ? ` · assigned by ${assignment.assignedByUserName}` : ""}
                                      {assignment.source === "SelfSignup" ? " · self signup" : ""}
                                    </div>
                                    {assignment.note && <p className="mt-1 text-xs">{assignment.note}</p>}
                                  </div>
                                ))}
                              </div>
                            )}

                            <div className="mt-3 grid gap-3 md:grid-cols-[1fr_auto]">
                              <div className="grid gap-2 md:grid-cols-2">
                                <select
                                  value={assignmentForms[task.id]?.assignedUserId || ""}
                                  onChange={(event) =>
                                    setAssignmentForms({
                                      ...assignmentForms,
                                      [task.id]: {
                                        ...assignmentForms[task.id],
                                        assignedUserId: event.target.value,
                                      },
                                    })
                                  }
                                  className="rounded border border-emerald-200 px-3 py-2"
                                  aria-label="Assign a group member"
                                >
                                  <option value="">Assign a member...</option>
                                  {members.map((member) => (
                                    <option key={member.id} value={member.id}>{member.name}</option>
                                  ))}
                                </select>
                                <Input
                                  value={assignmentForms[task.id]?.displayName || ""}
                                  onChange={(event) =>
                                    setAssignmentForms({
                                      ...assignmentForms,
                                      [task.id]: {
                                        ...assignmentForms[task.id],
                                        displayName: event.target.value,
                                      },
                                    })
                                  }
                                  placeholder="Or external helper name"
                                />
                                <Input
                                  value={assignmentForms[task.id]?.roleName || ""}
                                  onChange={(event) =>
                                    setAssignmentForms({
                                      ...assignmentForms,
                                      [task.id]: {
                                        ...assignmentForms[task.id],
                                        roleName: event.target.value,
                                      },
                                    })
                                  }
                                  placeholder="Role: cocoa server, driver, setup..."
                                />
                                <Input
                                  value={assignmentForms[task.id]?.note || ""}
                                  onChange={(event) =>
                                    setAssignmentForms({
                                      ...assignmentForms,
                                      [task.id]: {
                                        ...assignmentForms[task.id],
                                        note: event.target.value,
                                      },
                                    })
                                  }
                                  placeholder="Visibility note"
                                />
                              </div>
                              <Button type="button" size="sm" onClick={() => handleAssignHelper(task)}>
                                Assign helper
                              </Button>
                            </div>

                            <div className="mt-3 flex flex-wrap items-center gap-3 rounded border border-emerald-200 bg-white/80 p-2">
                              <QrCode className="h-5 w-5" />
                              <span className="text-xs">Recruit with task signup link</span>
                              <code className="break-all text-xs">{getTaskSignupUrl(task)}</code>
                              <img
                                alt={`QR code for ${task.title} task signup`}
                                className="h-20 w-20 rounded bg-white p-1"
                                src={`https://api.qrserver.com/v1/create-qr-code/?size=120x120&margin=8&data=${encodeURIComponent(getTaskSignupUrl(task))}`}
                              />
                            </div>
                          </div>
                        )}

                        {task.needsFunds && (
                          <div className="mt-3 rounded-md border border-yellow-200 bg-yellow-50 p-3 text-sm text-yellow-950">
                            <div className="font-medium">Funding still needed</div>
                            <p>
                              {formatFundingAmount(task.fundingShortfall, task.fundingCurrency || "USD")} still needed for this task.
                              Record cash/check receipts in Stewardship or give online when enabled.
                            </p>
                          </div>
                        )}

                        {bitcoinEnabled && needsCompletionRecipient && (
                          <div className="mt-3 space-y-2">
                            <Label htmlFor={`complete-recipient-${task.id}`}>Recipient BTC Address</Label>
                            <Input
                              id={`complete-recipient-${task.id}`}
                              value={completePaymentRecipients[task.id] || ""}
                              onChange={(event) =>
                                setCompletePaymentRecipients({
                                  ...completePaymentRecipients,
                                  [task.id]: event.target.value,
                                })
                              }
                              placeholder="tb1..."
                            />
                          </div>
                        )}

                        {commands.some((item) => item.command === "complete") && readinessItems.length > 0 && (
                          <div className="mt-3 rounded-md border border-slate-200 bg-slate-50 p-3 text-sm">
                            <div className="font-medium text-slate-950">Before marking this done</div>
                            <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-2 text-slate-900">
                              {Number(task.fundingNeededAmount || 0) > 0 && (
                                <label className="flex items-center gap-2">
                                  <input
                                    type="checkbox"
                                    checked={Boolean(completionProof[task.id]?.fundingConfirmed)}
                                    onChange={(event) =>
                                      setCompletionProof({
                                        ...completionProof,
                                        [task.id]: {
                                          ...completionProof[task.id],
                                          fundingConfirmed: event.target.checked,
                                        },
                                      })
                                    }
                                  />
                                  Money is confirmed
                                </label>
                              )}
                              {task.materialsNeeded && (
                                <label className="flex items-center gap-2">
                                  <input
                                    type="checkbox"
                                    checked={Boolean(completionProof[task.id]?.materialsConfirmed)}
                                    onChange={(event) =>
                                      setCompletionProof({
                                        ...completionProof,
                                        [task.id]: {
                                          ...completionProof[task.id],
                                          materialsConfirmed: event.target.checked,
                                        },
                                      })
                                    }
                                  />
                                  Materials are ready
                                </label>
                              )}
                              {Number(task.volunteerSlotsNeeded || 0) > 0 && (
                                <label className="flex items-center gap-2">
                                  <input
                                    type="checkbox"
                                    checked={Boolean(completionProof[task.id]?.volunteersConfirmed)}
                                    onChange={(event) =>
                                      setCompletionProof({
                                        ...completionProof,
                                        [task.id]: {
                                          ...completionProof[task.id],
                                          volunteersConfirmed: event.target.checked,
                                        },
                                      })
                                    }
                                  />
                                  Helpers are confirmed
                                </label>
                              )}
                              {task.receiptRequired && (
                                <label className="flex items-center gap-2">
                                  <input
                                    type="checkbox"
                                    checked={Boolean(completionProof[task.id]?.receiptAttached)}
                                    onChange={(event) =>
                                      setCompletionProof({
                                        ...completionProof,
                                        [task.id]: {
                                          ...completionProof[task.id],
                                          receiptAttached: event.target.checked,
                                        },
                                      })
                                    }
                                  />
                                  Receipt or proof is attached
                                </label>
                              )}
                            </div>
                            <Textarea
                              className="mt-3 min-h-[70px]"
                              value={completionProof[task.id]?.completionProofNote || ""}
                              onChange={(event) =>
                                setCompletionProof({
                                  ...completionProof,
                                  [task.id]: {
                                    ...completionProof[task.id],
                                    completionProofNote: event.target.value,
                                  },
                                })
                              }
                              placeholder="Add a quick note if anything was donated, borrowed, bought with cash, or still needs a receipt."
                            />
                          </div>
                        )}

                        {commands.some((item) => item.command === "skip") && (
                          <div className="mt-3 rounded-md border border-slate-200 bg-slate-50 p-3 text-sm">
                            <Label htmlFor={`skip-reason-${task.id}`}>If this is no longer needed, say why</Label>
                            <Textarea
                              id={`skip-reason-${task.id}`}
                              className="mt-2 min-h-[70px]"
                              value={skipReasons[task.id] || ""}
                              onChange={(event) =>
                                setSkipReasons({
                                  ...skipReasons,
                                  [task.id]: event.target.value,
                                })
                              }
                              placeholder="Example: We decided to skip the ornament table because no one could supply materials, and the event can still move forward."
                            />
                          </div>
                        )}

                        {commands.length > 0 && (
                          <div className="mt-3 flex flex-wrap justify-end gap-2">
                            {commands.map((item) => (
                              <Button
                                key={item.command}
                                type="button"
                                size="sm"
                                variant={item.command === "approve-completion" ? "default" : item.command === "skip" ? "secondary" : "outline"}
                                onClick={() => handleTaskCommand(task, item.command)}
                              >
                                {item.label}
                              </Button>
                            ))}
                          </div>
                        )}
                      </div>
                    )
                  })}

                  {taskCommandError && (
                    <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">
                      {taskCommandError}
                    </div>
                  )}

                  <div className="mt-4 flex justify-end">
                    <Button onClick={() => setActiveTab("suggestions")} variant="outline">
                      <Edit className="mr-2 h-4 w-4" /> Suggest Edit
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="suggestions">
          <Card className="mb-4">
            <CardContent className="p-4">
              <h3 className="text-lg font-medium mb-1">What needs to happen?</h3>
              <p className="mb-4 text-sm text-muted-foreground">
                Add the ordinary steps, supplies, helpers, and money this idea will need before it can be done.
              </p>

              <div className="space-y-4">
                {newSuggestion.map((task, index) => (
                  <div key={index} className="border rounded-lg p-4 space-y-3">
                    <div className="flex justify-between items-center">
                      <h4 className="font-medium">Task #{index + 1}</h4>
                      {index > 0 && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRemoveTask(index)}
                          className="text-red-500 h-6 w-6 p-0"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor={`title-${index}`}>What needs to be done?</Label>
                      <Input
                        id={`title-${index}`}
                        value={task.title}
                        onChange={(e) => handleTaskChange(index, "title", e.target.value)}
                        placeholder="Hot cocoa stand, caroling route, setup tables..."
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor={`description-${index}`}>What should this include?</Label>
                      <Textarea
                        id={`description-${index}`}
                        value={task.description}
                        onChange={(e) => handleTaskChange(index, "description", e.target.value)}
                        placeholder="Add the details people should know before they volunteer or vote for this plan."
                        className="min-h-[80px]"
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <div className="space-y-2">
                        <Label htmlFor={`priority-${index}`}>Priority</Label>
                        <select
                          id={`priority-${index}`}
                          value={task.priority}
                          onChange={(e) => handleTaskChange(index, "priority", e.target.value)}
                          className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                        >
                          <option value="high">High</option>
                          <option value="medium">Medium</option>
                          <option value="low">Low</option>
                        </select>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor={`hours-${index}`}>Estimated Hours</Label>
                        <Input
                          id={`hours-${index}`}
                          type="number"
                          value={task.estimatedHours}
                          onChange={(e) => handleTaskChange(index, "estimatedHours", e.target.value)}
                          placeholder="Enter hours"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor={`assigned-${index}`}>Assigned To (Optional)</Label>
                        <select
                          id={`assigned-${index}`}
                          value={task.assignedTo}
                          onChange={(e) => handleTaskChange(index, "assignedTo", e.target.value)}
                          className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                        >
                          <option value="">Unassigned</option>
                          {members.map((member) => (
                            <option key={member.id} value={member.id}>
                              {member.name}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor={`executor-${index}`}>Executor (Optional)</Label>
                        <select
                          id={`executor-${index}`}
                          value={task.executorId}
                          onChange={(e) => handleTaskChange(index, "executorId", e.target.value)}
                          className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                        >
                          <option value="">Unassigned</option>
                          {members.map((member) => (
                            <option key={member.id} value={member.id}>
                              {member.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    {bitcoinEnabled && <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div className="space-y-2">
                        <Label htmlFor={`funding-needed-${index}`}>Does this need money?</Label>
                        <Input
                          id={`funding-needed-${index}`}
                          type="number"
                          min="0"
                          step="0.01"
                          value={task.fundingNeededAmount}
                          onChange={(e) => handleTaskChange(index, "fundingNeededAmount", e.target.value)}
                          placeholder="50.00"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor={`materials-needed-${index}`}>What will we need?</Label>
                        <Input
                          id={`materials-needed-${index}`}
                          value={task.materialsNeeded}
                          onChange={(e) => handleTaskChange(index, "materialsNeeded", e.target.value)}
                          placeholder="Table, coffee maker, cups, cocoa mix"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor={`volunteers-needed-${index}`}>How many helpers?</Label>
                        <Input
                          id={`volunteers-needed-${index}`}
                          type="number"
                          min="0"
                          step="1"
                          value={task.volunteerSlotsNeeded}
                          onChange={(e) => handleTaskChange(index, "volunteerSlotsNeeded", e.target.value)}
                          placeholder="2"
                        />
                      </div>

                      <label className="flex items-center gap-2 rounded-md border border-gray-300 px-3 py-2 text-sm">
                        <input
                          type="checkbox"
                          checked={Boolean(task.receiptRequired)}
                          onChange={(e) => handleTaskChange(index, "receiptRequired", e.target.checked)}
                        />
                        This will need a receipt or proof
                      </label>
                    </div>}

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div className="space-y-2">
                        <Label htmlFor={`payment-amount-${index}`}>BTC Payout (Optional)</Label>
                        <Input
                          id={`payment-amount-${index}`}
                          type="number"
                          min="0"
                          step="0.00000001"
                          value={task.paymentAmount}
                          onChange={(e) => handleTaskChange(index, "paymentAmount", e.target.value)}
                          placeholder="0.00000000"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor={`payment-recipient-${index}`}>Recipient BTC Address</Label>
                        <Input
                          id={`payment-recipient-${index}`}
                          value={task.paymentRecipientAddress}
                          onChange={(e) => handleTaskChange(index, "paymentRecipientAddress", e.target.value)}
                          placeholder="tb1..."
                        />
                      </div>
                    </div>
                  </div>
                ))}

                <Button type="button" variant="outline" onClick={handleAddTask} className="w-full">
                  <Plus className="h-4 w-4 mr-2" /> Add Another Task
                </Button>

                <div className="flex justify-end">
                  <Button onClick={handleSubmitSuggestion}>Submit Task Suggestions</Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {suggestions.length === 0 ? (
            <p className="text-muted-foreground text-center py-4">
              No task suggestions yet. Be the first to suggest tasks!
            </p>
          ) : (
            <div className="space-y-4">
              {suggestions.map((suggestion) => (
                <Card key={suggestion.id}>
                  <CardContent className="p-4">
                    <div className="flex items-start gap-4">
                      <Avatar>
                        <AvatarImage src={suggestion.user?.avatarUrl || "/placeholder.svg"} />
                        <AvatarFallback>{suggestion.user?.name?.substring(0, 2) || "U"}</AvatarFallback>
                      </Avatar>
                      <div className="flex-1">
                        <div className="flex justify-between items-center mb-1">
                          <span className="font-medium">{suggestion.user?.name || "Anonymous"}</span>
                          <span className="text-xs text-muted-foreground">
                            {new Date(suggestion.createdAt).toLocaleString()}
                          </span>
                        </div>

                        <div className="space-y-3 mb-4">
                          {suggestion.tasks &&
                            suggestion.tasks.map((task, index) => (
                              <div key={index} className="border rounded-md p-3 bg-gray-50">
                                <div className="flex items-center justify-between">
                                  <h4 className="font-medium">{task.title}</h4>
                                  <span
                                    className={`text-xs px-2 py-0.5 rounded-full ${getPriorityColor(task.priority)}`}
                                  >
                                    {task.priority}
                                  </span>
                                </div>

                                {task.description && <p className="text-xs mt-1">{task.description}</p>}

                                <div className="mt-2 grid grid-cols-2 gap-1 text-xs">
                                  <div>
                                    <span className="font-medium">Hours:</span> {task.estimatedHours || "N/A"}
                                  </div>
                                  {task.assignedTo && (
                                    <div>
                                      <span className="font-medium">Assigned:</span> {task.assignedTo}
                                    </div>
                                  )}
                                  {task.executorId && (
                                    <div>
                                      <span className="font-medium">Executor:</span> {task.executorId}
                                    </div>
                                  )}
                                    {bitcoinEnabled && task.paymentAmount && (
                                    <div>
                                      <span className="font-medium">BTC:</span> {formatBtcPaymentAmount(task.paymentAmount)}
                                    </div>
                                  )}
                                  {task.fundingNeededAmount && (
                                    <div>
                                      <span className="font-medium">Needs:</span>{" "}
                                      {formatFundingAmount(task.fundingNeededAmount, task.fundingCurrency || "USD")}
                                    </div>
                                  )}
                                  {task.materialsNeeded && (
                                    <div>
                                      <span className="font-medium">Materials:</span> {task.materialsNeeded}
                                    </div>
                                  )}
                                  {task.volunteerSlotsNeeded && (
                                    <div>
                                      <span className="font-medium">Helpers:</span> {task.volunteerSlotsNeeded}
                                    </div>
                                  )}
                                  {task.paymentRecipientAddress && (
                                    <div>
                                      <span className="font-medium">Recipient:</span>{" "}
                                      {shortenPaymentIdentifier(task.paymentRecipientAddress)}
                                    </div>
                                  )}
                                </div>
                              </div>
                            ))}
                        </div>

                        <div className="flex justify-end gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleVote(suggestion.id, "up")}
                            className={suggestion.userVote === "up" ? "bg-green-50" : ""}
                          >
                            <ThumbsUp className="h-4 w-4 mr-1" />
                            <span>{suggestion.upvotes || 0}</span>
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleVote(suggestion.id, "down")}
                            className={suggestion.userVote === "down" ? "bg-red-50" : ""}
                          >
                            <ThumbsDown className="h-4 w-4 mr-1" />
                            <span>{suggestion.downvotes || 0}</span>
                          </Button>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="discussion">
          <Card className="mb-4">
            <CardContent className="p-4">
              <div className="space-y-4 max-h-96 overflow-y-auto mb-4">
                {comments.length === 0 ? (
                  <p className="text-muted-foreground text-center py-4">No comments yet. Start the discussion!</p>
                ) : (
                  comments.map((comment) => (
                    <div key={comment.id} className="flex gap-3 p-3 rounded-lg bg-gray-50">
                      <Avatar>
                        <AvatarImage src={comment.user?.avatarUrl || "/placeholder.svg"} />
                        <AvatarFallback>{comment.user?.name?.substring(0, 2) || "U"}</AvatarFallback>
                      </Avatar>
                      <div className="flex-1">
                        <div className="flex justify-between items-center mb-1">
                          <span className="font-medium">{comment.user?.name || "Anonymous"}</span>
                          <span className="text-xs text-muted-foreground">
                            {new Date(comment.createdAt).toLocaleString()}
                          </span>
                        </div>
                        <p className="text-sm">{comment.content}</p>
                      </div>
                    </div>
                  ))
                )}
              </div>

              <div className="space-y-2">
                <Textarea
                  placeholder="Add to the discussion..."
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  className="min-h-[100px]"
                />
                <div className="flex justify-end">
                  <Button onClick={handleSubmitComment}>
                    <MessageSquare className="mr-2 h-4 w-4" /> Post Comment
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  )
}

export default TaskListModule
